import { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { sendChatMessage, sendStreamingChatMessage, resetConversation } from '../services/api/azureAgent';
import { checkChatLimit, registerChatMessage } from '../services/api/chatService';

// Chat hook sem base prompt no frontend. Toda a instrução fica no agente.
const useChatBot = () => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);
  const abortControllerRef = useRef(null);

  // Função para recarregar o limite
  const reloadLimit = useCallback(async () => {
    try {
      const limitCheck = await checkChatLimit();
      setLimitInfo(limitCheck);
    } catch (err) {
      console.warn('Erro ao carregar limite:', err);
    }
  }, []);

  // Inicializar com mensagem de boas-vindas
  const initializeChat = useCallback(() => {
    const welcomeMessage = {
      role: 'assistant',
      content: t('ChatBot.WelcomeMessage'),
      timestamp: new Date()
    };
    setMessages([welcomeMessage]);
  }, [t]);

  // Enviar mensagem normal
  const sendMessage = useCallback(async (content) => {
    if (!content.trim()) return;

    // Verificar limite antes de enviar
    try {
      const limitCheck = await checkChatLimit();
      setLimitInfo(limitCheck);

      if (!limitCheck.canSendMessage) {
        setError(t('ChatBot.LimitExceeded', { limit: limitCheck.limit, current: limitCheck.currentCount }));
        return;
      }
    } catch (err) {
      console.warn('Erro ao verificar limite, continuando:', err);
    }

    const userMessage = {
      role: 'user',
      content: content.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    try {
      const allMessages = [...messages, userMessage];
      const response = await sendChatMessage(allMessages);

      const assistantMessage = {
        role: 'assistant',
        content: response.content,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Registrar mensagem em paralelo (não bloquear o fluxo)
      registerChatMessage(userMessage.content).catch(err => {
        console.warn('Erro ao registrar mensagem:', err);
      });

      // Atualizar limite após enviar
      const updatedLimit = await checkChatLimit();
      setLimitInfo(updatedLimit);
    } catch (err) {
      setError(err.message);
      console.error('Erro ao enviar mensagem:', err);
    } finally {
      setIsLoading(false);
    }
  }, [messages, t]);

  // Encerra a mensagem em streaming: mantém o texto parcial ou descarta se vazia
  const finalizeStreamingMessage = useCallback(() => {
    setMessages(prev => {
      const lastMessage = prev[prev.length - 1];
      if (!lastMessage?.isStreaming) return prev;

      if (!lastMessage.content) {
        return prev.slice(0, -1);
      }

      const newMessages = [...prev];
      const { isStreaming, ...finalized } = lastMessage;
      newMessages[newMessages.length - 1] = finalized;
      return newMessages;
    });
  }, []);

  // Enviar mensagem com streaming
  const sendStreamingMessage = useCallback(async (content) => {
    if (!content.trim()) return;

    // Verificar limite antes de enviar
    try {
      const limitCheck = await checkChatLimit();
      setLimitInfo(limitCheck);

      if (!limitCheck.canSendMessage) {
        setError(t('ChatBot.LimitExceeded', { limit: limitCheck.limit, current: limitCheck.currentCount }));
        return;
      }
    } catch (err) {
      console.warn('Erro ao verificar limite, continuando:', err);
    }

    const userMessage = {
      role: 'user',
      content: content.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setIsStreaming(true);
    setError(null);

    // Criar mensagem temporária para o assistente
    const tempAssistantMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true
    };

    setMessages(prev => [...prev, tempAssistantMessage]);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      const allMessages = [...messages, userMessage];

      await sendStreamingChatMessage(
        allMessages,
        (content) => {
          // Atualizar mensagem em tempo real
          setMessages(prev => {
            const lastMessage = prev[prev.length - 1];
            // Pode não existir se o chat foi limpo durante a geração
            if (!lastMessage?.isStreaming) return prev;

            const newMessages = [...prev];
            newMessages[newMessages.length - 1] = {
              ...lastMessage,
              content: lastMessage.content + content,
            };
            return newMessages;
          });
        },
        async (response) => {
          // Finalizar streaming
          setMessages(prev => {
            const lastMessage = prev[prev.length - 1];
            if (!lastMessage?.isStreaming) return prev;

            const newMessages = [...prev];
            const { isStreaming, ...finalized } = lastMessage;
            newMessages[newMessages.length - 1] = {
              ...finalized,
              content: response.content,
            };
            return newMessages;
          });

          // Registrar mensagem em paralelo (não bloquear o fluxo)
          registerChatMessage(userMessage.content).catch(err => {
            console.warn('Erro ao registrar mensagem:', err);
          });

          // Atualizar limite após enviar
          const updatedLimit = await checkChatLimit();
          setLimitInfo(updatedLimit);
        },
        (err) => {
          // Cancelamento é ação do usuário, não erro a ser exibido
          if (abortController.signal.aborted) {
            finalizeStreamingMessage();
            return;
          }
          finalizeStreamingMessage();
          setError(err.message);
          console.error('Erro no streaming:', err);
        },
        abortController.signal
      );
    } catch (err) {
      if (abortController.signal.aborted) {
        finalizeStreamingMessage();
      } else {
        setError(err.message);
        console.error('Erro ao enviar mensagem com streaming:', err);
      }
    } finally {
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
      setIsLoading(false);
      setIsStreaming(false);
    }
  }, [messages, finalizeStreamingMessage]);

  const clearError = useCallback(() => setError(null), []);

  // Limpar chat
  const clearChat = useCallback(() => {
    // Aborta o que estiver em andamento: sem isso a resposta continua
    // chegando e reaparece na conversa que o usuário acabou de limpar
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    resetConversation();
    setMessages([]);
    setError(null);
    setIsLoading(false);
    setIsStreaming(false);
  }, []);

  // Cancelar requisição em andamento
  const cancelRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsLoading(false);
    setIsStreaming(false);
  }, []);

  return {
    messages,
    isLoading,
    isStreaming,
    error,
    clearError,
    limitInfo,
    sendMessage,
    sendStreamingMessage,
    clearChat,
    cancelRequest,
    initializeChat,
    reloadLimit,
  };
};

export default useChatBot;
